/**
 * 权限控制组件
 * 用于条件渲染和权限保护
 */
import React from 'react';
import usePermissionStore from '../../stores/permissionStore';

/**
 * PermissionGuard - 权限守卫组件
 * 根据权限控制子组件的渲染
 * 
 * @example
 * // 单个权限
 * <PermissionGuard permission="students:student:create">
 *   <button>创建学员</button>
 * </PermissionGuard>
 * 
 * // 多个权限（OR 逻辑）
 * <PermissionGuard permissions={['students:student:edit', 'students:student:delete']}>
 *   <button>操作</button>
 * </PermissionGuard>
 * 
 * // 显示降级内容
 * <PermissionGuard permission="finance:record:create" fallback={<span>无权限</span>}>
 *   <button>添加财务记录</button>
 * </PermissionGuard>
 */
export function PermissionGuard({ 
  permission, 
  permissions = [], 
  children, 
  fallback = null,
  disabled = false, // 是否使用禁用模式而不是隐藏
}) {
  const store = usePermissionStore();
  
  // 合并单个权限和多个权限
  const requiredPermissions = permission 
    ? [permission, ...permissions] 
    : permissions;
  
  // 空权限数组表示不需要权限检查
  if (requiredPermissions.length === 0) {
    return children;
  }
  
  // OR 逻辑：满足任一权限即可
  const hasAccess = requiredPermissions.some(p => store.hasPermission(p));
  
  if (hasAccess) {
    return children;
  }
  
  // 禁用模式
  if (disabled && React.isValidElement(children)) {
    return React.cloneElement(children, {
      disabled: true,
      className: `${children.props.className || ''} opacity-50 cursor-not-allowed`,
    });
  }
  
  return fallback;
}

/**
 * RoleGuard - 角色守卫组件
 * 根据角色控制子组件的渲染
 * 
 * @example
 * <RoleGuard roles={['admin', 'teacher']}>
 *   <AdminPanel />
 * </RoleGuard>
 */
export function RoleGuard({ 
  role, 
  roles = [], 
  children, 
  fallback = null 
}) {
  const store = usePermissionStore();
  
  // 合并单个角色和多个角色
  const allowedRoles = role ? [role, ...roles] : roles;
  
  // 空角色数组表示不需要角色检查
  if (allowedRoles.length === 0) {
    return children;
  }
  
  const hasAccess = allowedRoles.includes(store.role);
  
  return hasAccess ? children : fallback;
}

/**
 * AuthGuard - 认证守卫组件
 * 检查用户是否已登录
 * 
 * @example
 * <AuthGuard fallback={<LoginPrompt />}>
 *   <Dashboard />
 * </AuthGuard>
 */
export function AuthGuard({ children, fallback = null }) {
  const store = usePermissionStore();
  
  return store.isLoggedIn ? children : fallback;
}

/**
 * withPermission - 权限高阶组件
 * 包装组件添加权限控制
 * 
 * @example
 * const ProtectedButton = withPermission(Button, 'students:student:create');
 */
export function withPermission(WrappedComponent, permissionId, options = {}) {
  const { fallback = null, disabled = false } = options;
  
  return function PermissionWrappedComponent(props) {
    return (
      <PermissionGuard 
        permission={permissionId} 
        fallback={fallback}
        disabled={disabled}
      >
        <WrappedComponent {...props} />
      </PermissionGuard>
    );
  };
}

/**
 * PermissionButton - 带权限控制的按钮
 * 
 * @example
 * <PermissionButton 
 *   permission="students:student:create"
 *   onClick={handleCreate}
 *   className="btn-primary"
 * >
 *   创建学员
 * </PermissionButton>
 */
export function PermissionButton({ 
  permission, 
  permissions = [],
  children, 
  disableMode = false, // true: 禁用按钮, false: 隐藏按钮
  ...buttonProps 
}) {
  const store = usePermissionStore();
  
  const requiredPermissions = permission 
    ? [permission, ...permissions] 
    : permissions;
  
  // 空权限数组表示不需要权限检查
  if (requiredPermissions.length === 0) {
    return <button {...buttonProps}>{children}</button>;
  }
  
  const hasAccess = requiredPermissions.some(p => store.hasPermission(p));
  
  if (!hasAccess) {
    if (disableMode) {
      return (
        <button 
          {...buttonProps} 
          disabled 
          className={`${buttonProps.className || ''} opacity-50 cursor-not-allowed`}
        >
          {children}
        </button>
      );
    }
    return null;
  }
  
  return <button {...buttonProps}>{children}</button>;
}

export default {
  PermissionGuard,
  RoleGuard,
  AuthGuard,
  withPermission,
  PermissionButton,
};
